import React, { useEffect, useState } from 'react';
import { Table, Card, Typography, Tag, Space, Button, message, Select, Alert } from 'antd';
import { ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
};

const { Title, Text } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const MerchantCustodialWalletRequests: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || '');
  const [requestTypeFilter, setRequestTypeFilter] = useState<string>(searchParams.get('requestType') || '');

  const pageId = parseInt(searchParams.get('pageId') || '1');
  const perPage = parseInt(searchParams.get('perPage') || '10');

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, perPage, statusFilter, requestTypeFilter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params: any = {
        pageId,
        perPage,
      };
      if (statusFilter) {
        params.status = statusFilter;
      }
      if (requestTypeFilter) {
        params.requestType = requestTypeFilter;
      }

      const response = await apiClient.get(
        API_ENDPOINTS.MERCHANT_GET_UPDATE_REQUESTS,
        { params }
      );

      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        setRequests(data.itemsList || []);
        const paginator = data.paginator || data.pagination;
        if (paginator) {
          setPagination({
            current: paginator.currentPage || pageId,
            pageSize: paginator.perPage || perPage,
            total:
              paginator.itemCount ||
              paginator.totalCount ||
              paginator.total ||
              (data.itemsList || []).length,
          });
        }
      }
    } catch (error: any) {
      console.error('Error fetching account modify requests:', error);
      message.error(
        error.response?.data?.message ||
          'Failed to fetch account modify requests'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination: any) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageId', newPagination.current.toString());
    newParams.set('perPage', newPagination.pageSize.toString());
    setSearchParams(newParams);
  };

  const columns = [
    {
      title: 'Request Type',
      dataIndex: 'requestType',
      key: 'requestType',
      render: (type: string) => {
        const typeMap: Record<string, { label: string; color: string }> = {
          CUSTODIAL_WALLET_CHANGE: { label: 'Custodial Wallet Change', color: 'blue' },
          UNKNOWN_TOKEN_SWEEP_REQUEST: { label: 'Unknown Token Sweep', color: 'purple' },
        };
        const typeInfo = typeMap[type] || { label: type, color: 'default' };
        return <Tag color={typeInfo.color}>{typeInfo.label}</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color =
          status === 'APPROVED'
            ? 'green'
            : status === 'REJECTED'
            ? 'red'
            : 'gold';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Requested By',
      key: 'requestedBy',
      render: (_: any, record: any) => {
        if (record.requestedByMerchant) {
          return (
            <div>
              <div style={{ fontWeight: 500 }}>{record.requestedByMerchant.email}</div>
              {record.requestedByMerchant.firstName || record.requestedByMerchant.lastName ? (
                <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>
                  {[record.requestedByMerchant.firstName, record.requestedByMerchant.lastName].filter(Boolean).join(' ')}
                </div>
              ) : null}
            </div>
          );
        }
        return <Text type="secondary" style={{ fontSize: '12px' }}>Admin</Text>;
      },
    },
    {
      title: 'Requested At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string) =>
        value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: 'Processed At',
      dataIndex: 'processedAt',
      key: 'processedAt',
      render: (value: string | null) =>
        value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: 'Details',
      key: 'details',
      render: (_: any, record: any) => {
        if (record.requestType === 'CUSTODIAL_WALLET_CHANGE') {
          const before = record.payloadBefore;
          const after = record.payloadAfter;
          const beforeParts: string[] = [];
          const afterParts: string[] = [];
          
          if (before) {
            if (before.custodialEVMAddress) beforeParts.push(`EVM: ${before.custodialEVMAddress.substring(0, 10)}...`);
            if (before.bitcoinCustodialWallet) beforeParts.push(`BTC: ${before.bitcoinCustodialWallet.substring(0, 10)}...`);
            if (before.solanaCustodialWallet) beforeParts.push(`SOL: ${before.solanaCustodialWallet.substring(0, 10)}...`);
            if (before.tronCustodialWallet) beforeParts.push(`TRX: ${before.tronCustodialWallet.substring(0, 10)}...`);
          }
          
          if (after) {
            if (after.custodialEVMAddress) afterParts.push(`EVM: ${after.custodialEVMAddress.substring(0, 10)}...`);
            if (after.bitcoinCustodialWallet) afterParts.push(`BTC: ${after.bitcoinCustodialWallet.substring(0, 10)}...`);
            if (after.solanaCustodialWallet) afterParts.push(`SOL: ${after.solanaCustodialWallet.substring(0, 10)}...`);
            if (after.tronCustodialWallet) afterParts.push(`TRX: ${after.tronCustodialWallet.substring(0, 10)}...`);
          }
          
          return (
            <div>
              {beforeParts.length > 0 && (
                <div style={{ marginBottom: 4 }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>From: {beforeParts.join(', ')}</Text>
                </div>
              )}
              {afterParts.length > 0 && (
                <div>
                  <Text style={{ fontSize: '12px' }}>To: {afterParts.join(', ')}</Text>
                </div>
              )}
            </div>
          );
        } else if (record.requestType === 'UNKNOWN_TOKEN_SWEEP_REQUEST') {
          const payload = record.payloadAfter;
          if (!payload) return '-';
          
          const details: string[] = [];
          if (payload.walletId) details.push(`Wallet: ${payload.walletId.substring(0, 8)}...`);
          if (payload.currencySymbol) details.push(`Currency: ${payload.currencySymbol}`);
          if (payload.contractAddress) details.push(`Contract: ${payload.contractAddress === 'native' ? 'native' : payload.contractAddress.substring(0, 10) + '...'}`);
          if (payload.amount) details.push(`Amount: ${payload.amount}`);
          if (payload.blockchainName) details.push(`Chain: ${payload.blockchainName}`);
          
          return (
            <div>
              {details.map((detail, idx) => (
                <div key={idx} style={{ marginBottom: idx < details.length - 1 ? 4 : 0 }}>
                  <Text style={{ fontSize: '12px' }}>{detail}</Text>
                </div>
              ))}
            </div>
          );
        }
        return '-';
      },
    },
    {
      title: 'Admin Comment',
      dataIndex: 'adminComment',
      key: 'adminComment',
      render: (comment: string | null) => comment || '-',
      ellipsis: true,
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          Update Requests
        </Title>
        <Space>
          <Select
            allowClear
            placeholder="All Statuses"
            value={statusFilter || undefined}
            onChange={(value) => {
              setStatusFilter(value || '');
              const newParams = new URLSearchParams(searchParams);
              if (value) {
                newParams.set('status', value);
              } else {
                newParams.delete('status');
              }
              newParams.set('pageId', '1');
              setSearchParams(newParams);
            }}
            style={{ minWidth: 180 }}
          >
            <Select.Option value="PENDING">Pending</Select.Option>
            <Select.Option value="APPROVED">Approved</Select.Option>
            <Select.Option value="REJECTED">Rejected</Select.Option>
          </Select>
          <Select
            allowClear
            placeholder="All Types"
            value={requestTypeFilter || undefined}
            onChange={(value) => {
              setRequestTypeFilter(value || '');
              const newParams = new URLSearchParams(searchParams);
              if (value) {
                newParams.set('requestType', value);
              } else {
                newParams.delete('requestType');
              }
              newParams.set('pageId', '1');
              setSearchParams(newParams);
            }}
            style={{ minWidth: 220 }}
          >
            <Select.Option value="CUSTODIAL_WALLET_CHANGE">Custodial Wallet Change</Select.Option>
            <Select.Option value="UNKNOWN_TOKEN_SWEEP_REQUEST">Unknown Token Sweep</Select.Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={fetchRequests}>
            Refresh
          </Button>
        </Space>
      </HeaderContainer>

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
          marginBottom: 24,
        }}
      >
        <Alert
          message={<span style={{ color: COLORS.textPrimary }}>About Update Requests</span>}
          description={
            <div style={{ color: COLORS.textPrimary }}>
              <Text style={{ color: COLORS.textPrimary }}>
                This page shows all your update requests that require admin approval, including:
              </Text>
              <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20, color: COLORS.textPrimary }}>
                <li style={{ color: COLORS.textPrimary }}><strong style={{ color: COLORS.textPrimary }}>Custodial Wallet Change:</strong> Requests to update your custodial wallet addresses (EVM, Bitcoin, Solana)</li>
                <li style={{ color: COLORS.textPrimary }}><strong style={{ color: COLORS.textPrimary }}>Unknown Token Sweep:</strong> Requests to retrieve funds from unsupported contract addresses</li>
              </ul>
              <Text style={{ color: COLORS.textPrimary, display: 'block', marginTop: 8 }}>
                All requests must be approved by an administrator before they are processed. You can see which merchant user in your company made each request.
              </Text>
            </div>
          }
          type="info"
          icon={<InfoCircleOutlined />}
          showIcon
          style={{
            backgroundColor: COLORS.bgSecondary,
            border: `1px solid ${COLORS.border}`,
          }}
        />
      </Card>

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <div style={{ marginBottom: 12, color: COLORS.textSecondary }}>
          Total {pagination.total} requests
        </div>
        <TableScrollWrapper>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={requests}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: window.innerWidth > 768,
              showTotal: (total, range) => (
                <span style={{ color: COLORS.textSecondary }}>
                  Showing {range[0]}-{range[1]} of {total} requests
                </span>
              ),
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            style={{ background: 'transparent' }}
            size="small"
            scroll={{ x: 'max-content' }}
          />
        </TableScrollWrapper>
      </Card>
    </PageContainer>
  );
};

export default MerchantCustodialWalletRequests;


